import { useProgress } from '@react-three/drei';
import { useEffect } from 'react';
import { useSceneStore } from '../store/useSceneStore';

/**
 * UI-01: Loading overlay bound to drei's `useProgress` hook.
 *
 * Visibility gate: stays visible until BOTH drei's asset loader finished AND
 * the manifest has loaded. drei's `useProgress.active` covers glb/ktx2/texture
 * loaders inside <Canvas>; `useSceneStore.manifestLoaded` covers JSON (which
 * does not flow through drei's loaders).
 *
 * AESTHETIC-BRIEF Rule 7: period-quiet serif (Goudy Old Style / Cormorant).
 * AESTHETIC-BRIEF Rule 3: warm-ochre / cream palette, clay-orange progress bar.
 */
export function LoadingHUD() {
  const { active, progress } = useProgress();
  const manifestLoaded = useSceneStore((s) => s.manifestLoaded);
  const setLoadProgress = useSceneStore((s) => s.setLoadProgress);

  useEffect(() => {
    setLoadProgress(progress);
  }, [progress, setLoadProgress]);

  if (!active && manifestLoaded) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(180deg, #FDF6EE 0%, #E8D5B7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        fontFamily:
          '"Goudy Old Style", "Cormorant Garamond", Georgia, serif',
        color: '#3B2F2F',
        zIndex: 100,
      }}
      aria-label="Loading Edgewood Lake"
      role="status"
    >
      <div
        style={{
          fontSize: '2rem',
          marginBottom: '1.5rem',
          letterSpacing: '0.05em',
        }}
      >
        Edgewood Lake
      </div>
      <div
        style={{
          width: 320,
          height: 4,
          background: 'rgba(59,47,47,0.2)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.max(progress, manifestLoaded ? 100 : 10)}%`,
            height: '100%',
            background: '#B8602C',
            transition: 'width 240ms ease-out',
            borderRadius: 2,
          }}
        />
      </div>
      <div
        style={{ marginTop: '0.75rem', fontSize: '0.875rem', opacity: 0.6 }}
      >
        {manifestLoaded && !active ? '100%' : `${Math.floor(progress)}%`}
      </div>
    </div>
  );
}
